import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Lock, User, Mail, ShieldCheck, ArrowRight, Eye, EyeOff, Info } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import useSeo from "../hooks/useSeo";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MemberLogin = () => {
  useSeo({
    title: "Member Login",
    description: "Sign in to your Astoria Solutions member portal.",
    path: "/member-login",
  });
  const { toast } = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/member";
  const initialTab = params.get("tab") === "register" ? "register" : "login";
  const [mode, setMode] = useState(initialTab); // login | register
  const [step, setStep] = useState("credentials"); // credentials | otp
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", email: "", full_role: "" });
  const [otp, setOtp] = useState("");
  const [txId, setTxId] = useState(null);
  const [otpNotice, setOtpNotice] = useState(null);

  useEffect(() => {
    // If already signed in (either JWT token or Google session cookie), skip.
    if (localStorage.getItem("astoria_member_token")) {
      navigate(redirect, { replace: true });
      return;
    }
    (async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        navigate(redirect, { replace: true });
      } catch { /* not signed in */ }
    })();
  }, [navigate, redirect]);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const persistAuth = (data) => {
    localStorage.setItem("astoria_member_token", data.token);
    localStorage.setItem("astoria_member_user", JSON.stringify(data.user));
    toast({ title: `Welcome, ${data.user.username}`, description: "You are signed in." });
    navigate(redirect, { replace: true });
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) {
      toast({ title: "Missing fields", description: "Enter username and password." });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, {
        username: form.username,
        password: form.password,
      });
      if (data.requires_otp) {
        setTxId(data.tx_id);
        setStep("otp");
        toast({ title: "Enter the code", description: data.message || "Check your email." });
      } else {
        setOtpNotice(data.otp_status);
        persistAuth(data);
      }
    } catch (err) {
      toast({
        title: "Sign-in failed",
        description: err?.response?.data?.detail || "Please check your credentials.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password || !form.email) {
      toast({ title: "Missing fields", description: "Username, email and password are required." });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/register`, form);
      persistAuth(data);
    } catch (err) {
      toast({
        title: "Sign-up failed",
        description: err?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (otp.length < 4) {
      toast({ title: "Enter the code" });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-otp`, { tx_id: txId, code: otp });
      persistAuth(data);
    } catch (err) {
      toast({
        title: "OTP failed",
        description: err?.response?.data?.detail || "Try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="min-h-[80vh] bg-neutral-50 flex items-center py-16">
      <div className="max-w-5xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
        {/* Left panel */}
        <div className="bg-black text-white rounded-3xl p-10 flex flex-col justify-between overflow-hidden relative">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-teal-500/20 blur-3xl" />
          <div className="relative">
            <Logo variant="light" />
            <h2 className="mt-10 text-3xl font-extrabold leading-tight">Members-only insights, delivered clearly.</h2>
            <p className="mt-4 text-neutral-300 leading-relaxed">
              Sign in to access the private Astoria portal — briefings, engagement documents and long-form research written exclusively for our clients.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-neutral-200">
              <li className="flex items-center gap-3"><ShieldCheck className="w-4 h-4 text-teal-400" /> End-to-end encryption</li>
              <li className="flex items-center gap-3"><ShieldCheck className="w-4 h-4 text-teal-400" /> Two-factor login via email OTP</li>
              <li className="flex items-center gap-3"><ShieldCheck className="w-4 h-4 text-teal-400" /> Zero data sold, ever</li>
            </ul>
          </div>
          <p className="relative mt-10 text-xs text-neutral-500">© 2025 ASTORIA SOLUTIONS</p>
        </div>

        {/* Right form panel */}
        <div className="bg-white rounded-3xl p-8 md:p-10 border border-neutral-100 shadow-sm">
          {step === "credentials" && (
            <>
              <div className="inline-flex bg-neutral-100 rounded-full p-1 mb-6">
                <button
                  onClick={() => setMode("login")}
                  className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                    mode === "login" ? "bg-white text-neutral-900 shadow" : "text-neutral-500"
                  }`}
                >
                  Sign in
                </button>
                <button
                  onClick={() => setMode("register")}
                  className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                    mode === "register" ? "bg-white text-neutral-900 shadow" : "text-neutral-500"
                  }`}
                >
                  Create account
                </button>
              </div>

              <h1 className="text-3xl font-extrabold text-neutral-900">
                {mode === "login" ? "Welcome back" : "Create your account"}
              </h1>
              <p className="mt-2 text-sm text-neutral-500">
                {mode === "login"
                  ? "Sign in to your Astoria Solutions member portal."
                  : "It only takes a minute."}
              </p>

              <form onSubmit={mode === "login" ? submitLogin : submitRegister} className="mt-6 space-y-4">
                <label className="block">
                  <span className="text-xs font-semibold text-neutral-700">Username</span>
                  <div className="mt-1 relative">
                    <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input
                      name="username"
                      value={form.username}
                      onChange={change}
                      className="w-full pl-9 pr-3 py-3 rounded-lg border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                      placeholder="your.username"
                    />
                  </div>
                </label>

                {mode === "register" && (
                  <>
                    <label className="block">
                      <span className="text-xs font-semibold text-neutral-700">Email</span>
                      <div className="mt-1 relative">
                        <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                        <input
                          name="email"
                          type="email"
                          value={form.email}
                          onChange={change}
                          className="w-full pl-9 pr-3 py-3 rounded-lg border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                          placeholder="you@company.com"
                        />
                      </div>
                    </label>
                    <label className="block">
                      <span className="text-xs font-semibold text-neutral-700">Role at your company (optional)</span>
                      <input
                        name="full_role"
                        value={form.full_role}
                        onChange={change}
                        className="mt-1 w-full px-3 py-3 rounded-lg border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                        placeholder="e.g. Head of Finance"
                      />
                    </label>
                  </>
                )}

                <label className="block">
                  <span className="text-xs font-semibold text-neutral-700">Password</span>
                  <div className="mt-1 relative">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input
                      name="password"
                      type={showPwd ? "text" : "password"}
                      value={form.password}
                      onChange={change}
                      className="w-full pl-9 pr-10 py-3 rounded-lg border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                      placeholder="Minimum 6 characters"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-800"
                      aria-label="Toggle password"
                    >
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-semibold py-3 rounded-full transition-colors disabled:opacity-60"
                >
                  {submitting ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </form>

              {/* Continue with Google (Emergent-managed) */}
              <div className="mt-6">
                <div className="flex items-center gap-3 text-[10px] font-semibold tracking-widest text-neutral-400">
                  <span className="flex-1 h-px bg-neutral-200" />
                  OR
                  <span className="flex-1 h-px bg-neutral-200" />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                    const redirectUrl = window.location.origin + "/member";
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                  className="mt-4 w-full inline-flex items-center justify-center gap-3 border border-neutral-200 hover:border-teal-500 hover:bg-neutral-50 text-neutral-800 font-semibold py-3 rounded-full transition-colors"
                >
                  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#EA4335" d="M24 9.5c3.3 0 6.3 1.2 8.6 3.2l6.4-6.4C34.9 2.5 29.8 0 24 0 14.6 0 6.4 5.4 2.5 13.2l7.5 5.8C11.9 13 17.5 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.2-.4-4.7H24v9h12.7c-.5 3-2.2 5.5-4.7 7.2l7.3 5.7c4.3-3.9 6.8-9.7 6.8-17.2z"/>
                    <path fill="#FBBC05" d="M10 28.9c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.5-5.8C.9 17 0 20.4 0 24s.9 7 2.5 10.1L10 28.9z"/>
                    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.3-5.7c-2 1.4-4.6 2.3-8.6 2.3-6.6 0-12.2-4.5-14.2-10.6l-7.5 5.8C6.4 42.6 14.6 48 24 48z"/>
                  </svg>
                  Continue with Google
                </button>
              </div>

              {/* OTP notice section */}
              <div className="mt-6 rounded-xl border border-dashed border-teal-300 bg-teal-50/50 p-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-4 h-4 text-teal-600 mt-0.5" />
                  <div className="text-xs text-neutral-700 leading-relaxed">
                    <p className="font-semibold text-neutral-900">Two-factor via email OTP</p>
                    <p className="mt-1">
                      After sign-in, a 6-digit code will be emailed to you and required to complete access. This step will activate automatically once SMTP is configured on the server.
                    </p>
                  </div>
                </div>
              </div>

              {otpNotice && (
                <p className="mt-3 text-xs text-neutral-500 flex items-center gap-1"><Info className="w-3 h-3" /> {otpNotice}</p>
              )}

              <p className="mt-6 text-xs text-neutral-500 text-center">
                By continuing you agree to our terms and privacy policy.
              </p>
              <p className="mt-2 text-xs text-neutral-500 text-center">
                <Link to="/" className="hover:text-teal-600">← Back to home</Link>
              </p>
            </>
          )}

          {step === "otp" && (
            <>
              <h1 className="text-3xl font-extrabold text-neutral-900">Enter your code</h1>
              <p className="mt-2 text-sm text-neutral-500">
                We emailed a 6-digit code to complete your sign-in.
              </p>
              <form onSubmit={submitOtp} className="mt-6 space-y-4">
                <input
                  autoFocus
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  placeholder="123456"
                  className="w-full text-center tracking-[0.5em] text-2xl font-bold py-4 rounded-lg border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-semibold py-3 rounded-full transition-colors disabled:opacity-60"
                >
                  Verify & Sign in
                </button>
                <button
                  type="button"
                  onClick={() => { setStep("credentials"); setOtp(""); }}
                  className="w-full text-xs text-neutral-500 hover:text-neutral-800"
                >
                  Back to sign-in
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default MemberLogin;
